import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import {
  categoriaEfetiva,
  divergenciaGrande,
  lineIsConsistent,
  reviewTotals,
  tamanhoDiverge,
  totalPorCategoria,
  valorEfetivo,
  valoresDaLinha,
} from '@domain/receipt'
import type { Receipt, ReceiptItem } from '@domain/receipt'
import type { Product } from '@domain/product'
import { chaveDaDescricao, devePerguntar } from '@domain/finance'
import type { CategoryRule, Category, RuleDecision, Transaction } from '@domain/finance'
import { SeletorDeCategoria } from '@ui/finance/SeletorDeCategoria'
import { formatBRL } from '@shared/money/format'
import { Glass } from '@ui/glass/Glass'
import { Voltar } from '@ui/shell/Voltar'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import type { ReceiptItemPatch } from '@domain/ports/receipt-repository'
import { useLive } from '@ui/lists/use-live'
import { VincularTransacao } from '@ui/notas/VincularTransacao'
import { vincularNota } from '@ui/notas/vincular'
import { PerguntaDeRegra } from '@ui/finance/PerguntaDeRegra'
import { LinhaDaNota } from '@ui/notas/LinhaDaNota'
import { AcoesDaLinha } from '@ui/notas/AcoesDaLinha'
import { EditarLinha } from '@ui/notas/EditarLinha'
import { EscolherProduto } from '@ui/notas/EscolherProduto'
import { ItemManual } from '@ui/notas/ItemManual'
import { ResumoDaConfirmacao } from '@ui/notas/ResumoDaConfirmacao'
import { mensagemDeConfirmada, previaDaConfirmacao } from '@ui/notas/resumo-da-confirmacao'
import { Botao } from '@ui/glass/Botao'
import { fraseDaDivergencia, nomeDaLinha } from '@ui/notas/linha'
import { irAteLinha } from '@ui/notas/ir-ate-linha'
import { IconeMais } from '@ui/shell/icones'
import { DESKTOP_AMPLO, useMidia } from '@ui/shell/use-midia'

/**
 * A revisão — *"a tela que define a fase"*.
 *
 * ## O `rawText` fica visível, sempre
 *
 * *"Lista de itens, cada um com o `rawText` original visível e o produto
 * sugerido."* Ele é a evidência: é o que a pessoa compara com o cupom na mão
 * para decidir, e é o que nunca se edita. Uma tela que mostrasse só o produto
 * sugerido pediria confiança cega numa sugestão de IA — que é exatamente o que o
 * princípio 5 recusa.
 *
 * ## Nada financeiro muda antes de "Confirmar"
 *
 * As mudanças de vínculo escrevem no `ReceiptItem`, que é rascunho. Breakdown,
 * ponto de preço e aprendizado só nascem quando a nota vira `confirmed` — e
 * quem os grava é `onReceiptConfirmed`, com Admin SDK. Enquanto esta tela está
 * aberta, o orçamento não mudou.
 *
 * ## A diferença aparece EXPLÍCITA
 *
 * *"A diferença entre a soma dos itens e o total da nota aparece explícita."*
 * Ela não é escondida nem corrigida sozinha: *"geralmente é desconto ou item
 * ilegível, e o usuário resolve"*. Um app que fechasse a conta sozinho estaria
 * inventando um item.
 *
 * ## A CATEGORIA é da linha, e não do produto
 *
 * O primeiro documento real que não era cupom de mercado foi um **boleto de
 * condomínio**: "Taxa de condomínio", "Fundo de reserva", "Consumo de água".
 * Nenhum deles é produto, nenhum vai ser, e todos são dinheiro que precisa cair
 * numa categoria.
 *
 * Por isso cada linha tem seu seletor, sempre visível — e não escondido atrás de
 * "trocar produto". O produto continua existindo, mas ele decide **catálogo**
 * (preço, aprendizado); quem decide **orçamento** é a categoria.
 *
 * E ela fica visível porque é ela que move dinheiro: confirmar a nota aplica as
 * categorias que estão na tela, então elas têm de estar na tela.
 *
 * ## Sem transação, a nota fica INCOMPLETA e a tela diz isso
 *
 * *"Uma nota fiscal sempre deve estar vinculada a uma transação."* Ela pode
 * subir sem — o sync roda uma vez por dia, e quem fotografa o cupom no
 * estacionamento não tem a transação ainda —, mas isso é um estado de
 * passagem, não um destino.
 *
 * O aviso aparece **antes** de confirmar, e não depois: sem transação a
 * confirmação não gera detalhamento, e descobrir isso depois de confirmar é
 * descobrir tarde. Vincular depois também funciona (`onReceiptConfirmed` cobre
 * essa transição), mas avisar antes poupa a viagem.
 *
 * ## A foto fica visível ao lado dos itens
 *
 * `Receipt.md` diz que a imagem *"nunca é apagada depois de `confirmed`. É a
 * prova do que foi extraído"* — e prova que ninguém consegue olhar não prova
 * nada. Aqui ela serve **durante** a revisão: dá para conferir o `rawText`
 * contra o cupom sem pegar o papel, que é exatamente o gesto que a revisão pede.
 *
 * ## A tela lê por ASSINATURA porque a nota muda sozinha
 *
 * Ela abre em `uploaded` e a Cloud Function a leva a `extracted` segundos
 * depois. Sem assinatura, a pessoa teria de atualizar a página para ver se a IA
 * já leu.
 */

export function Nota() {
  const { id } = useParams({ from: '/protegidas/notas/$id' })
  const {
    receiptRepository,
    productRepository,
    categoryRepository,
    transactionRepository,
    categoryRuleRepository,
  } = useFinanceRepositories()

  const assinarNota = useCallback(
    (onChange: (r: Receipt[]) => void, onError: (e: unknown) => void) =>
      receiptRepository.observe(
        id,
        (nota) => {
          onChange(nota === null ? [] : [nota])
        },
        onError,
      ),
    [receiptRepository, id],
  )
  const assinarLinhas = useCallback(
    (onChange: (i: ReceiptItem[]) => void, onError: (e: unknown) => void) =>
      receiptRepository.observeItems(id, onChange, onError),
    [receiptRepository, id],
  )

  const notaLive = useLive<Receipt>(assinarNota)
  const linhasLive = useLive<ReceiptItem>(assinarLinhas)
  const amplo = useMidia(DESKTOP_AMPLO)

  /**
   * A folha aberta sobre uma linha — ações, edição ou troca de produto.
   *
   * **Mora na tela, e não na linha**, porque a folha precisa ser desenhada fora
   * do vidro da lista: `backdrop-filter` faz do cartão a referência de todo
   * `fixed` lá dentro (decisão 242e), e a folha cobriria só o cartão. Guarda o
   * **id**, e não a linha: a linha muda ao vivo enquanto a folha está aberta.
   */
  const [aberta, setAberta] = useState<{
    tipo: 'acoes' | 'editar' | 'trocar'
    linhaId: string
  } | null>(null)

  /**
   * A folha de item manual. `linhaId: null` é uma linha nova.
   *
   * Estado próprio, e não um `tipo` a mais em `aberta`: as outras três folhas
   * só existem sobre uma linha que já existe, e o bloco que as desenha é
   * guardado por isso. Adicionar não tem linha nenhuma por baixo.
   */
  const [itemManual, setItemManual] = useState<{ linhaId: string | null } | null>(null)

  /**
   * O resumo antes de gravar está aberto — decisão 305. E `acabouDeConfirmar`
   * existe só para trazer a mensagem final à vista: ela nasce no topo da tela,
   * e o "Confirmar" mora no fim de uma lista longa. A crítica 302 mediu o aviso
   * nascendo ~120px acima da viewport — a pessoa via os botões sumirem e nada
   * mais.
   */
  const [confirmando, setConfirmando] = useState(false)
  const [acabouDeConfirmar, setAcabouDeConfirmar] = useState(false)

  const [regras, setRegras] = useState<CategoryRule[]>([])
  const [pergunta, setPergunta] = useState<
    { descricao: string; categoria: string; key: string; categoryId: string } | null
  >(null)
  const [produtos, setProdutos] = useState<Product[]>([])
  const [categorias, setCategorias] = useState<Category[]>([])
  useEffect(() => {
    let vivo = true
    void Promise.all([productRepository.list(), categoryRepository.list()])
      .then(([lidos, cats]) => {
        if (!vivo) return
        setProdutos(lidos)
        setCategorias(cats)
      })
      .catch((e: unknown) => {
        console.error('Nota: catálogo ou categorias não carregaram', e)
      })
    return () => {
      vivo = false
    }
  }, [productRepository, categoryRepository])

  useEffect(() => {
    let vivo = true
    void categoryRuleRepository
      .list()
      .then((lidas) => {
        if (vivo) setRegras(lidas)
      })
      .catch((erro: unknown) => {
        // Sem a tabela, a revisão continua inteira: o que se perde é a
        // pergunta, nunca a categorização.
        console.error('Nota: a tabela de regras não carregou', erro)
      })
    return () => {
      vivo = false
    }
  }, [categoryRuleRepository])

  /**
   * Vincular, e levar a categoria da nota junto — a regra mora em
   * `vincularNota`, porque o detalhe da transação também vincula.
   */
  const vincular = useCallback(
    async (transacao: Transaction, categoriaDaNota: string | null) => {
      await vincularNota(
        { receiptRepository, transactionRepository },
        { id, categoryId: categoriaDaNota },
        transacao,
      )
    },
    [receiptRepository, transactionRepository, id],
  )

  const nota = notaLive.status === 'loaded' ? (notaLive.items[0] ?? null) : null
  const linhas = linhasLive.status === 'loaded' ? linhasLive.items : []
  const totais = reviewTotals(linhas, nota?.extracted?.totalCents ?? null)
  const linhasQueNaoFecham = linhas.filter(
    (l) => l.matchStatus !== 'rejected' && !lineIsConsistent(valoresDaLinha(l)),
  ).length
  const porId = new Map(produtos.map((p) => [p.id, p]))
  const produtoDe = (linha: ReceiptItem) =>
    linha.productId === null ? null : (porId.get(linha.productId) ?? null)
  const linhaAberta = aberta === null ? null : (linhas.find((l) => l.id === aberta.linhaId) ?? null)

  /**
   * O que o "aceitar em lote" pode aceitar — decisão 304.
   *
   * **Só sugestão de verdade**: linha pendente COM produto. A sem produto fica
   * como está; antes ela virava "pulada" de carona, num botão que prometia
   * aceitar sugestões. Para uma linha sem produto, pendente e pulada gravam a
   * mesma coisa na confirmação, então pular não comprava nada além do rótulo
   * mentindo.
   *
   * **E só a que bate com o cupom.** Uma lata de 350ml sugerida para um cupom
   * que diz 310ML grava o preço de uma no histórico da outra. Ela sai do lote
   * e a linha diz as duas medidas; o ✓ da própria linha continua lá, porque
   * pode ser o cadastro que está errado — e quem decide é quem olha o papel.
   */
  const sugeridas = linhas.filter((l) => l.matchStatus === 'pending' && l.productId !== null)
  const comTamanhoDiferente = sugeridas.filter((l) => {
    const p = produtoDe(l)
    return p !== null && tamanhoDiverge(l.rawText, p)
  })
  const aceitaveis = sugeridas.filter((l) => !comTamanhoDiferente.includes(l))

  /**
   * As linhas que esperam alguém — decisão 310. O MESMO critério que acende a
   * faixa na linha (`LinhaDaNota`): sugestão ainda não decidida, com o produto
   * carregado. A sem produto já entra na soma e não pede nada.
   */
  const aConferir = linhas.filter((l) => l.matchStatus === 'pending' && produtoDe(l) !== null)
  const conferidas = linhas.length - aConferir.length

  /**
   * A próxima a conferir DEPOIS de onde a pessoa está, e não sempre a primeira:
   * quem já desceu até a linha 20 não quer ser jogado de volta à 3. Sem nenhuma
   * abaixo, volta ao começo.
   */
  const irParaProxima = () => {
    const abaixo = aConferir.find((l) => {
      const el = document.getElementById(`linha-${l.id}`)
      return el !== null && el.getBoundingClientRect().top > 96
    })
    const alvo = abaixo ?? aConferir[0]
    if (alvo !== undefined) irAteLinha(alvo.id)
  }
  const decidir = (linha: ReceiptItem, patch: ReceiptItemPatch) => {
    void receiptRepository.updateItem(id, linha.id, patch)

    // A pergunta "valer sempre?" vale para o ITEM (Fase 10, etapa 4): a linha
    // que nunca vai ser produto — taxa de serviço, sacola, frete — só tem a
    // tabela de regras para resolver sozinha na próxima nota. Categorizar item
    // COM produto não pergunta: quem manda ali é a categoria do produto, e a
    // regra passaria por cima dela sem ninguém pedir.
    const categoryId = patch.categoryId
    if (categoryId === undefined || categoryId === null) return
    if (linha.productId !== null) return
    if (!devePerguntar(linha.rawText, regras, 'item')) return
    setPergunta({
      descricao: linha.rawText,
      categoria: categorias.find((c) => c.id === categoryId)?.name ?? 'esta categoria',
      key: chaveDaDescricao(linha.rawText),
      categoryId,
    })
  }

  /** Grava a resposta da barra e recarrega a tabela. */
  const responder = (decision: RuleDecision) => {
    const atual = pergunta
    setPergunta(null)
    if (atual === null) return
    void categoryRuleRepository
      .upsert({
        key: atual.key,
        label: atual.descricao,
        decision,
        origin: 'learned',
        // `item`, e não `both`: "sacola" numa nota é uma linha de compra, e no
        // extrato seria uma transação de outra natureza. Ensinar os dois de uma
        // vez seria decidir por uma pergunta que ninguém fez.
        scope: 'item',
      })
      .then(() => categoryRuleRepository.list())
      .then(setRegras)
      .catch((erro: unknown) => {
        console.error('Nota: a regra não foi gravada', erro)
      })
  }

  const statusDaNota = nota?.status
  useEffect(() => {
    if (!acabouDeConfirmar || statusDaNota !== 'confirmed') return
    // `?.()` porque o jsdom não implementa `scrollIntoView` — e um teste que
    // quebra aqui não estaria testando nada da tela.
    document.getElementById('nota-confirmada')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }, [acabouDeConfirmar, statusDaNota])

  if (notaLive.status === 'loading') {
    return <p className="text-sm text-[var(--text-muted)]">Carregando…</p>
  }
  if (nota === null) {
    return (
      <section>
        <Voltar para="/notas">Notas</Voltar>
        <p className="text-sm">Nota não encontrada.</p>
      </section>
    )
  }

  // O que confirmar vai gravar, pela mesma conta da Function. Ver
  // `previaDaConfirmacao`.
  const previa = previaDaConfirmacao({
    linhas,
    produtoDe,
    nota,
    totais,
    semCategoriaCents: semCategoriaCents(linhas, porId, nota.categoryId),
  })

  return (
    // **No desktop largo, duas colunas** — as linhas à esquerda; à direita,
    // fixos enquanto se rola, a foto do cupom ABERTA e o fechamento. É o
    // gesto da tela: conferir linha a linha contra o papel. No celular a foto
    // vive recolhida porque empurraria a lista para baixo; com 1280px ela cabe
    // ao lado, e escondê-la seria esconder a evidência. E o fechamento fixo
    // põe o total e o "Confirmar" à vista numa nota de 41 linhas, em vez de no
    // fim de uma rolagem de 7.000px.
    //
    // `data-largura="ampla"` pede à casca a largura para isso (`casca.css`).
    <section
      data-largura="ampla"
      className="xl:grid xl:grid-cols-[minmax(0,1fr)_minmax(20rem,25rem)] xl:items-start xl:gap-6"
    >
      <div className="min-w-0">
        <Voltar para="/notas">Notas</Voltar>
        <header className="mb-4">
          <h1 className="titulo text-2xl">
            {nota.extracted?.merchantName ?? 'Nota fiscal'}
          </h1>
          {nota.extracted?.issuedAt != null && (
            <p className="text-sm text-[var(--text-muted)]">{`Emitida em ${nota.extracted.issuedAt}`}</p>
          )}
        </header>

        {nota.status !== 'failed' && (
          <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-[var(--text-muted)]">Categoria da nota</span>
            <div className="min-w-48 flex-1">
              <SeletorDeCategoria
                categorias={categorias}
                value={nota.categoryId}
                rotulo="Categoria da nota"
                disabled={fechada(nota)}
                onChange={(categoryId) => {
                  void receiptRepository.setCategory(id, categoryId)
                }}
                onCriou={() => {
                  void categoryRepository.list().then(setCategorias)
                }}
              />
            </div>
            {/* O que ela faz, dito onde se escolhe — e, quando não dá para mexer,
                POR QUE não dá. Um campo que não responde ao toque e não explica é
                o defeito, não a trava. */}
            <p className="w-full text-xs text-[var(--text-muted)]">
              {fechada(nota)
                ? 'O detalhamento desta nota já foi gravado com a transação, e mudar a categoria agora não o reescreveria. Dá para trocar a categoria direto na transação.'
                : 'Vale para as linhas que ficarem sem categoria, e vai junto para a transação quando você vincular — se ela ainda não tiver uma.'}
            </p>
          </div>
        )}

        {(nota.status === 'uploaded' || nota.status === 'processing') && (
          <Glass level="frost" className="p-4" as="section" aria-label="Lendo a nota">
            <p role="status" className="text-sm">
              Lendo a nota… Os itens aparecem aqui sozinhos quando terminar.
            </p>
          </Glass>
        )}

        {nota.status === 'failed' && (
          <Glass level="frost" className="p-4" as="section" aria-label="A leitura falhou">
            {/* "Nota que falha na extração mostra o erro e permite tentar de
                novo." O erro vai inteiro: ele é o que diz o que fazer. */}
            <p role="alert" className="mb-2 text-sm">
              Não deu para ler esta nota.
            </p>
            {nota.error !== null && (
              <p className="text-xs text-[var(--text-muted)]">{nota.error}</p>
            )}
            <p className="mt-2 text-xs text-[var(--text-muted)]">
              Mande outra foto em Notas — com o cupom plano e a luz de frente, costuma resolver.
            </p>
          </Glass>
        )}

        {nota.status === 'confirmed' && (
          <Glass
            level="frost"
            className="mb-4 scroll-mt-4 p-4"
            as="section"
            aria-label="Nota confirmada"
            id="nota-confirmada"
          >
            {/* Do que aconteceu, e não uma frase fixa: a antiga — "O
                detalhamento e os preços já estão gravados." — mentia sem
                transação e sem data. */}
            <p className="text-sm">{mensagemDeConfirmada(nota, previa.precos)}</p>
          </Glass>
        )}

        {nota.transactionId === null && nota.status !== 'failed' && (
          <Glass level="frost" className="mb-4 p-4" as="section" aria-label="Sem transação">
            {/* A cor da marca, igual ao cartão de "sem transação" em Notas: é o
                único bloco desta tela que pede alguma coisa de quem chegou. */}
            <h2 className="mb-1 text-sm font-medium text-[var(--brand)]">
              Esta nota ainda não tem transação
            </h2>
            <p className="mb-3 text-xs text-[var(--text-muted)]">
              {/* Só o que a falta de transação custa. Dizia que a nota "alimenta o
                  catálogo e o histórico de preço" — falso sem data, e era uma
                  de quatro frases que prometiam histórico (decisão 309). */}
              Sem ela, a nota não vira detalhamento de nenhuma compra.
            </p>
            <VincularTransacao
              nota={nota}
              onVincular={(transacao) => {
                void vincular(transacao, nota.categoryId)
              }}
            />
          </Glass>
        )}

        {nota.transactionId !== null && (
          <p className="mb-4 text-xs text-[var(--text-muted)]">
            Vinculada a{' '}
            <Link
              to="/transacoes/$id"
              params={{ id: nota.transactionId }}
              className="alvo justify-start underline"
            >
              uma transação
            </Link>
            .{' '}
            <button
              type="button"
              className="alvo justify-start underline"
              onClick={() => {
                void receiptRepository.linkTransaction(id, null)
              }}
            >
              Desvincular
            </button>
          </p>
        )}

        {!amplo && nota.thumbnailUrl !== null && (
          <Glass level="frost" className="mb-4 p-2" as="section" aria-label="Foto do cupom">
            <details className="group">
              {/* Fechada por padrão: numa tela de celular, a lista de itens é o
                  que a pessoa veio fazer, e a foto empurraria tudo para baixo.
                  Um toque abre — num alvo de 44px e com a seta das outras
                  dobras, e não no triângulo do navegador com 28px (decisão
                  311). */}
              <summary className="alvo flex w-full cursor-pointer list-none items-center justify-between gap-2 px-2 text-sm text-[var(--text-muted)]">
                Ver a foto do cupom
                <span aria-hidden="true" className="transition-transform group-open:rotate-90">
                  ›
                </span>
              </summary>
              <img
                src={nota.thumbnailUrl}
                alt="Cupom fiscal fotografado"
                className="mt-2 max-h-[70vh] w-full rounded-lg object-contain"
              />
            </details>
          </Glass>
        )}

        {(nota.status === 'extracted' || nota.status === 'confirmed') && (
          <>
            <Glass level="frost" className="mb-4 p-4" as="section" aria-label="Itens da nota">
              {nota.status === 'extracted' && linhas.length > 0 && (
                // **Quanto falta, e onde** — decisão 310. Antes, achar o que
                // esperava decisão era caçar um ponto de 6px numa lista longa.
                <div className="mb-1 flex min-h-11 items-center justify-between gap-2 text-xs">
                  {aConferir.length === 0 ? (
                    <span className="text-[var(--text-muted)]">Tudo conferido</span>
                  ) : (
                    <span>{`${String(conferidas)} de ${String(linhas.length)} ${linhas.length === 1 ? 'conferida' : 'conferidas'}`}</span>
                  )}
                  {aConferir.length > 0 && (
                    <button
                      type="button"
                      className="alvo px-2 font-medium text-[var(--brand)]"
                      onClick={irParaProxima}
                    >
                      Próxima a conferir
                    </button>
                  )}
                </div>
              )}
              {linhas.length === 0 ? (
                <p className="text-sm text-[var(--text-muted)]">
                  A leitura não encontrou item nenhum nesta nota.
                </p>
              ) : (
                <ul aria-label="Itens">
                  {linhas.map((linha) => (
                    <LinhaDaNota
                      key={linha.id}
                      linha={linha}
                      produto={produtoDe(linha)}
                      categorias={categorias}
                      categoriaDaNota={nota.categoryId}
                      somenteLeitura={nota.status === 'confirmed'}
                      onDecidir={(patch) => {
                        decidir(linha, patch)
                      }}
                      onCriouCategoria={() => {
                        void categoryRepository.list().then(setCategorias)
                      }}
                      onEditar={() => {
                        // A linha manual não tem cru para corrigir ao lado: o
                        // nome e o valor dela são tudo o que ela é, e quem os
                        // edita é a mesma folha que a criou.
                        if (linha.manual) setItemManual({ linhaId: linha.id })
                        else setAberta({ tipo: 'editar', linhaId: linha.id })
                      }}
                      onMaisAcoes={() => {
                        setAberta({ tipo: 'acoes', linhaId: linha.id })
                      }}
                    />
                  ))}
                </ul>
              )}

              {/* Acrescentar linha é gesto de EXCEÇÃO, e por isso fica no fim da
                  lista em vez de no cabeçalho: o trabalho da tela é conferir o
                  que a leitura trouxe, e este botão existe para o que ela não
                  tinha como trazer — o frete, a taxa, o abatimento do caixa. */}
              {nota.status === 'extracted' && (
                <button
                  type="button"
                  data-atalho-novo
                  className="alvo fileira-de-icones mt-1 w-full text-sm text-[var(--brand)]"
                  onClick={() => {
                    setItemManual({ linhaId: null })
                  }}
                >
                  <IconeMais />
                  Adicionar item
                </button>
              )}
            </Glass>
          </>
        )}
      </div>

      <div className="xl:sticky xl:top-4 xl:flex xl:max-h-[calc(100dvh-2rem)] xl:flex-col xl:gap-4">
        {amplo && nota.thumbnailUrl !== null && (
          // Aberta, e rolando por dentro: o cupom é comprido, e o fechamento
          // embaixo dela precisa continuar à vista.
          <Glass
            level="frost"
            className="flex min-h-48 flex-1 flex-col p-2"
            as="section"
            aria-label="Foto do cupom"
          >
            <div className="min-h-0 flex-1 overflow-y-auto rounded-lg">
              <img
                src={nota.thumbnailUrl}
                alt="Cupom fiscal fotografado"
                className="w-full rounded-lg"
              />
            </div>
          </Glass>
        )}

        {(nota.status === 'extracted' || nota.status === 'confirmed') && (
          <>
            <Glass
              level="frost"
              className="p-4 xl:max-h-[60dvh] xl:shrink-0 xl:overflow-y-auto"
              as="section"
              aria-label="Fechamento"
            >
              <dl className="mb-3 grid grid-cols-2 gap-2 text-sm">
                <dt className="text-[var(--text-muted)]">Soma dos itens</dt>
                <dd className="text-right tabular-nums">{formatBRL(totais.linesCents)}</dd>
                <dt className="text-[var(--text-muted)]">Total da nota</dt>
                <dd className="text-right tabular-nums">
                  {totais.receiptCents === null ? 'não lido' : formatBRL(totais.receiptCents)}
                </dd>
                {totais.differenceCents !== null && totais.differenceCents !== 0 && (
                  <>
                    {/* Explícita, e não corrigida sozinha. Ver o cabeçalho.
                        E com a DIREÇÃO: "Diferença R$ 23,94" não dizia se
                        faltava nos itens ou sobrava (decisão 311). */}
                    <dt>{totais.differenceCents > 0 ? 'Falta nos itens' : 'Sobra nos itens'}</dt>
                    <dd className="text-right tabular-nums">
                      {formatBRL(Math.abs(totais.differenceCents))}
                    </dd>
                  </>
                )}
              </dl>

              {divergenciaGrande(totais) && linhas.some((l) => l.matchStatus !== 'rejected') ? (
                // Longe o bastante para ser leitura errada, e não um desconto sem
                // dono. **E só com alguma linha:** numa nota em que a leitura não
                // achou item nenhum, "preço no item vizinho" seria mentira — e a
                // tela já diz, acima, que não houve item. Visto no navegador. Sem `role="alert"`: é conteúdo parado, e não um evento — e
                // a tela já usa o alerta para a nota que falhou.
                <p className="mb-3 text-sm" style={{ color: 'var(--pendente)' }}>
                  {`A soma dos itens está longe do total da nota. Quase sempre é leitura errada — preço no item vizinho ou quantidade perdida${
                    linhasQueNaoFecham === 0
                      ? ''
                      : `, e ${linhasQueNaoFecham === 1 ? '1 linha não fecha' : `${String(linhasQueNaoFecham)} linhas não fecham`} quantidade × unitário`
                  }. Confira os valores antes de confirmar${
                    // A ameaça só vale quando há preço a gravar — e quem sabe
                    // é a prévia, a mesma conta da Function (decisão 309).
                    previa.precos > 0 ? ': confirmada, a nota grava estes preços no histórico' : ''
                  }.`}
                </p>
              ) : (
                totais.differenceCents !== null &&
                totais.differenceCents !== 0 && (
                  <p className="mb-3 text-xs text-[var(--text-muted)]">
                    Costuma ser desconto no cupom ou item que não deu para ler. Dá para confirmar
                    assim — a diferença fica visível no detalhamento.
                  </p>
                )
              )}

              {nota.status === 'extracted' &&
                totais.differenceCents !== null &&
                totais.differenceCents < 0 && (
                  <AtribuirDesconto
                    linhas={linhas}
                    faltaCents={-totais.differenceCents}
                    onAtribuir={(itemId, discountCents) => {
                      void receiptRepository.updateItem(id, itemId, { discountCents })
                    }}
                  />
                )}

              {semCategoriaCents(linhas, porId, nota.categoryId) > 0 && (
                // O que vai sobrar sem classificação, dito antes de confirmar.
                // Ele não some: vai para o "resto" do breakdown, que é onde o não
                // classificado mora — mas saber quanto é ANTES muda a decisão de
                // parar e categorizar.
                <p className="mb-3 text-xs text-[var(--text-muted)]">
                  {`${formatBRL(semCategoriaCents(linhas, porId, nota.categoryId))} vão para “sem categoria” no detalhamento.`}
                </p>
              )}

              {nota.status === 'extracted' && nota.transactionId === null && (
                // Antes de confirmar, e não depois: confirmar sem transação não
                // gera detalhamento, e descobrir isso depois é descobrir tarde.
                <p className="mb-3 text-xs text-[var(--text-muted)]">
                  {nota.extracted?.issuedAt == null
                    ? 'Sem transação vinculada, confirmar não vai gerar o detalhamento da compra — só o catálogo. Dá para vincular depois, e aí ele é gerado.'
                    : 'Sem transação vinculada, confirmar não vai gerar o detalhamento da compra — só o catálogo e os preços. Dá para vincular depois, e aí ele é gerado.'}
                </p>
              )}

              {nota.status === 'extracted' && nota.extracted?.issuedAt == null && (
                // **A promessa que a tela não pode cumprir.** Um ponto de preço
                // sem data não entra em série nenhuma, então a nota sem data
                // alimenta o catálogo e o aprendizado, e não o histórico. Dizer
                // isso antes de confirmar é o mínimo: depois, a ausência seria um
                // preço que simplesmente nunca apareceu no gráfico.
                <p className="mb-3 text-xs text-[var(--text-muted)]">
                  A leitura não achou a data desta nota, então os preços dela não entram no
                  histórico — um preço sem data não dá para comparar com os de outro mês.
                </p>
              )}

              {nota.status === 'extracted' && comTamanhoDiferente.length > 0 && (
                // Leva até a linha que cita — "confira na linha" não dizia qual.
                // E sem "ficou fora do lote": sem nenhuma sugestão aceitável não
                // há lote na tela para ela ter ficado fora. Decisão 310.
                <p className="mb-3 flex flex-wrap items-center gap-x-2 text-xs" style={{ color: 'var(--pendente)' }}>
                  <span>
                    {comTamanhoDiferente.length === 1
                      ? '1 sugestão tem tamanho diferente do cupom.'
                      : `${String(comTamanhoDiferente.length)} sugestões têm tamanho diferente do cupom.`}
                  </span>
                  <button
                    type="button"
                    className="alvo px-1 font-medium underline underline-offset-2"
                    onClick={() => {
                      const primeira = comTamanhoDiferente[0]
                      if (primeira !== undefined) irAteLinha(primeira.id)
                    }}
                  >
                    {comTamanhoDiferente.length === 1 ? 'Ir até ela' : 'Ir até a primeira'}
                  </button>
                </p>
              )}

              {/* Dois pesos, e não um: "Confirmar" é a ação que termina a
                  tarefa, e era um botão cru do mesmo peso de "Aceitar" — a
                  pessoa não tinha como saber qual dos três verbos de aceitar
                  encerrava a revisão (crítica 302). */}
              {nota.status === 'extracted' && (
                <div className="flex flex-wrap gap-2">
                  <Botao
                    tom="primario"
                    type="button"
                    onClick={() => {
                      setConfirmando(true)
                    }}
                  >
                    Confirmar a nota
                  </Botao>
                  {aceitaveis.length > 0 && (
                    <Botao
                      type="button"
                      onClick={() => {
                        for (const linha of aceitaveis) {
                          void receiptRepository.updateItem(id, linha.id, {
                            matchStatus: 'confirmed',
                          })
                        }
                      }}
                    >
                      {aceitaveis.length === 1
                        ? 'Aceitar 1 sugestão'
                        : `Aceitar as ${String(aceitaveis.length)} sugestões`}
                    </Botao>
                  )}
                </div>
              )}
            </Glass>
          </>
        )}
      </div>

      {/* Fora dos vidros, de propósito — ver `aberta`. A nota confirmada não
          abre folha nenhuma: a linha nem oferece os botões, e uma folha que
          sobrasse aberta durante a confirmação gravaria por cima do que o
          detalhamento já registrou. */}
      {linhaAberta !== null && aberta !== null && nota.status === 'extracted' && (
        <>
          {aberta.tipo === 'acoes' && (
            <AcoesDaLinha
              linha={linhaAberta}
              nome={nomeDaLinha(linhaAberta, produtoDe(linhaAberta))}
              divergencia={
                linhaAberta.matchStatus === 'pending'
                  ? fraseDaDivergencia(linhaAberta, produtoDe(linhaAberta))
                  : null
              }
              onTrocar={() => {
                setAberta({ tipo: 'trocar', linhaId: linhaAberta.id })
              }}
              onDecidir={(patch) => {
                decidir(linhaAberta, patch)
              }}
              onApagar={() => {
                void receiptRepository.removeItem(id, linhaAberta.id)
              }}
              onFechar={() => {
                setAberta(null)
              }}
            />
          )}
          {aberta.tipo === 'editar' && (
            <EditarLinha
              linha={linhaAberta}
              nomeSemCorrecao={nomeDaLinha({ ...linhaAberta, edited: null }, produtoDe(linhaAberta))}
              onSalvar={(patch) => {
                decidir(linhaAberta, patch)
              }}
              onFechar={() => {
                setAberta(null)
              }}
            />
          )}
          {aberta.tipo === 'trocar' && (
            <EscolherProduto
              rawText={linhaAberta.rawText}
              produtos={produtos}
              atual={linhaAberta.productId}
              onEscolher={(p) => {
                // **`corrected: true`** — e é só isto que vira aprendizado.
                decidir(linhaAberta, {
                  productId: p.id,
                  categoryId: p.categoryId,
                  matchStatus: 'confirmed',
                  corrected: true,
                })
                setAberta(null)
              }}
              onFechar={() => {
                setAberta(null)
              }}
            />
          )}
        </>
      )}

      {confirmando && nota.status === 'extracted' && (
        <ResumoDaConfirmacao
          previa={previa}
          onIrAte={(linhaId) => {
            // A folha fecha antes, e o foco vai à linha no quadro seguinte —
            // senão ela cairia atrás da folha que ainda está desmontando.
            setConfirmando(false)
            window.setTimeout(() => {
              irAteLinha(linhaId)
            }, 0)
          }}
          onConfirmar={() => {
            setConfirmando(false)
            setAcabouDeConfirmar(true)
            void receiptRepository.confirm(id)
          }}
          onVoltar={() => {
            setConfirmando(false)
          }}
        />
      )}

      {itemManual !== null && nota.status === 'extracted' && (
        <ItemManual
          linha={
            itemManual.linhaId === null
              ? null
              : (linhas.find((l) => l.id === itemManual.linhaId) ?? null)
          }
          categorias={categorias}
          // A diferença que falta: quem abre isto numa nota que não fecha quase
          // sempre vai digitar exatamente este número. Negativa vem como
          // abatimento, que é o que ela quer dizer.
          sugestaoCents={totais.differenceCents ?? 0}
          teto={(categoryId) =>
            categoryId === null
              ? 0
              : (totalPorCategoria(
                  // **Sem a linha em edição**: um abatimento que já está
                  // gravado não pode ser o próprio teto dele.
                  linhas.filter((l) => l.id !== itemManual.linhaId),
                  (productId) => porId.get(productId) ?? null,
                  nota.categoryId,
                ).get(categoryId) ?? 0)
          }
          onSalvar={(entrada) => {
            if (itemManual.linhaId === null) {
              void receiptRepository.addItem(id, entrada)
            } else {
              void receiptRepository.updateItem(id, itemManual.linhaId, {
                manualLine: { name: entrada.name, totalCents: entrada.totalCents },
                categoryId: entrada.categoryId,
              })
            }
          }}
          onCriouCategoria={() => {
            void categoryRepository.list().then(setCategorias)
          }}
          onFechar={() => {
            setItemManual(null)
          }}
        />
      )}

      <PerguntaDeRegra
        pergunta={pergunta}
        onSempre={() => {
          if (pergunta !== null) {
            responder({ kind: 'category', categoryId: pergunta.categoryId })
          }
        }}
        onNunca={() => {
          responder({ kind: 'never' })
        }}
        onFechar={() => {
          setPergunta(null)
        }}
      />
    </section>
  )
}

/**
 * Dizer de quem é o desconto que a leitura não atribuiu.
 *
 * ## Por que a mão ainda é necessária
 *
 * A extração aprendeu a ler `desconto item 001` e a guardar o abatimento na
 * linha dele. Mas ela erra, e — mais importante — **toda nota lida antes disso
 * tem zero**: o desconto virava uma diferença sem dono, e o histórico guardava
 * o preço de etiqueta de uma coisa que saiu mais barata.
 *
 * ## Só as linhas que CABEM
 *
 * Desconto maior que o item é impossível, e oferecer a linha de R$ 5,99 para um
 * abatimento de R$ 8,00 seria oferecer um erro. Quem não cabe fica de fora — e
 * se ninguém couber, a lista não aparece, porque aí a diferença não é desconto
 * de item nenhum (é item ilegível, e a frase acima já explica isso).
 *
 * ## Fechada por padrão
 *
 * A diferença é comum e nem sempre é desconto. Aberta, ela empurraria os botões
 * de confirmar para fora da tela toda vez que a soma não fechasse.
 */
function AtribuirDesconto({
  linhas,
  faltaCents,
  onAtribuir,
}: {
  linhas: ReceiptItem[]
  faltaCents: number
  onAtribuir: (itemId: string, discountCents: number) => void
}) {
  const cabem = linhas.filter(
    (l) =>
      l.matchStatus !== 'rejected' &&
      l.discountCents === 0 &&
      valoresDaLinha(l).totalCents >= faltaCents,
  )
  if (cabem.length === 0) return null

  return (
    <details className="mb-3 border-t border-[var(--border)]">
      <summary className="alvo flex w-full cursor-pointer list-none items-center justify-between gap-2 text-xs text-[var(--brand)]">
        {`Foi desconto em um item? Diga qual, e ${formatBRL(faltaCents)} saem do preço dele`}
        <span aria-hidden="true">›</span>
      </summary>
      <ul aria-label="Linhas que podem ter o desconto">
        {cabem.map((l) => (
          <li key={l.id}>
            <button
              type="button"
              className="flex w-full items-baseline gap-2 border-b border-[var(--border)] py-2 text-left text-xs"
              aria-label={`O desconto foi em ${l.rawText}`}
              onClick={() => {
                onAtribuir(l.id, faltaCents)
              }}
            >
              <span className="min-w-0 flex-1 truncate font-mono">{l.rawText}</span>
              <span className="text-[var(--text-muted)] tabular-nums">
                {formatBRL(valoresDaLinha(l).totalCents)}
              </span>
              <span className="tabular-nums">{`→ ${formatBRL(valoresDaLinha(l).totalCents - faltaCents)}`}</span>
            </button>
          </li>
        ))}
      </ul>
    </details>
  )
}

/**
 * A categoria da nota ainda muda alguma coisa?
 *
 * **Confirmada NÃO basta para travar**, e a primeira versão travava assim —
 * o campo ficou morto na tela de uma nota confirmada e sem transação, que é
 * justamente o caso em que ele mais serve. O motivo está no gatilho da
 * `onReceiptConfirmed`, e não na tela:
 *
 * ```js
 * const vinculouDepois = antes.status === 'confirmed' && depois.status === 'confirmed'
 *   && vazio(antes.transactionId) && !vazio(depois.transactionId)
 * ```
 *
 * Confirmar recalcula o breakdown, e **vincular depois recalcula de novo**.
 * Então enquanto não há transação, mexer na categoria ainda alcança o que vai
 * ser gravado. Depois do vínculo é que a conta está fechada: as duas passagens
 * da Function já aconteceram, e nenhuma escrita na nota dispara uma terceira.
 */
function fechada(nota: Receipt): boolean {
  return nota.status === 'confirmed' && nota.transactionId !== null
}

/** Quanto vai para o resto por não ter categoria nenhuma. */
function semCategoriaCents(
  linhas: ReceiptItem[],
  porId: Map<string, Product>,
  categoriaDaNota: string | null,
): number {
  return linhas
    .filter((l) => l.matchStatus !== 'rejected')
    .filter(
      (l) =>
        categoriaEfetiva(
          l,
          l.productId === null ? null : (porId.get(l.productId) ?? null),
          categoriaDaNota,
        ) === null,
    )
    // `valorEfetivo`, e não o impresso: o que vai para o resto é dinheiro que
    // saiu da conta, e o desconto não saiu. Ficou 250,73 numa nota de 249,93
    // até isto ser conferido em produção.
    .reduce((soma, l) => soma + valorEfetivo(l), 0)
}
